import { observer } from 'mobx-react-lite'
import { type ManageItemsViewModel } from './ManageItemsViewModel'
import { match } from '@/utils/pattern-match-util'
import {
  CustomSelect,
  MultiItemSelectedDefault,
} from '@/components/inputs/CustomSelect'
import { useCustomSelectStateFromInfiniteScrollProvider } from '@/components/inputs/infinite-scroll-provider-hook'
import { SearchResultItem } from '@/components/inputs/SearchResultItem'
import { type JobScopeCustomItemViewModel } from '@/features/jobs/details/components/UpdateJobScopeDialog/ListItems/JobScopeCustomItem/JobScopeCustomItemViewModel'
import {
  ManageCustomItem,
  ManageItemsCustomServices,
  ManageItemsExistingService,
  ManageItemsInventoryItemOption,
  ManageItems,
} from '@/features/jobs/details/components/UpdateJobScopeDialog/ManageItems'

export interface ManageItemsContainerProps {
  vm: ManageItemsViewModel
}

export const ManageItemsContainer = observer(function ManageItemsContainer({
  vm,
}: ManageItemsContainerProps) {
  const inventoryItemProps = useCustomSelectStateFromInfiniteScrollProvider({
    scrollProvider: vm.inventoryItemScrollProvider,
    searchResults: vm.inventoryItemScrollProvider.items.slice(),
    searchText: vm.searchText.value,
    onSearchTextChange: vm.searchText.change,
  })

  return (
    <ManageItems
      showing={vm.modal.showing}
      mode={vm.mode}
      onModeChange={vm.setMode}
      onClose={vm.modal.close}
      onSubmit={vm.submit}
    >
      {match(vm.mode, {
        AnExistingService: () => (
          <ManageItemsExistingService
            inventoryItemSelect={
              <CustomSelect
                {...inventoryItemProps}
                placeholder="Select service items"
                itemToString={(v) => String(v?.name)}
                multi
                persist
                value={vm.inventoryItems}
                onChange={vm.setInventoryItems}
                renderItem={(itemProps) => (
                  <MultiItemSelectedDefault
                    {...itemProps}
                    item={itemProps}
                    itemToString={(i) => i.item?.name}
                  />
                )}
                renderSearchItem={({
                  item,
                  itemToString: _itemToString,
                  ...itemProps
                }) => (
                  <SearchResultItem
                    {...itemProps}
                    testId={
                      'job-scope-update-inventory-item-option-' +
                      item.name.toLowerCase()
                    }
                  >
                    <ManageItemsInventoryItemOption
                      name={item.name}
                      description={item.description}
                    />
                  </SearchResultItem>
                )}
                testId="job-scope-update-inventory-item-dropdown"
              />
            }
          />
        ),
        NewCustomService: () => (
          <ManageItemsCustomServices
            hasItems={vm.selectedCustomItems.length > 0}
            onAddAnother={vm.createCustomItem}
          >
            {vm.selectedCustomItems.map((item) => (
              <ManageCustomItemContainer vm={vm} itemVm={item} key={item.id} />
            ))}
          </ManageItemsCustomServices>
        ),
      })}
    </ManageItems>
  )
})

interface ManageCustomItemContainerProps {
  vm: ManageItemsViewModel
  itemVm: JobScopeCustomItemViewModel
}

const ManageCustomItemContainer = observer(function ManageCustomItemContainer({
  vm,
  itemVm,
}: ManageCustomItemContainerProps) {
  return (
    <ManageCustomItem
      name={itemVm.name}
      price={itemVm.price}
      proPrice={itemVm.proPrice}
      nameError={itemVm.validation.getError('name')}
      isFromJob={itemVm.source === 'Job'}
      onNameChange={itemVm.setName}
      onPriceChange={itemVm.setPrice}
      onProPriceChange={itemVm.setProPrice}
      onRemove={() => vm.removeSelectedCustomItem(itemVm)}
    />
  )
})
